import { useState, useEffect, useCallback, useRef } from "react";
import { collectionGroup, query, where, getDocs, doc, updateDoc, collection, addDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Icon from "./Icon";
import RestaurantsMap from "./RestaurantsMap";
import { extractCountryFromAddress, extractCityFromAddress } from "../utils";

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, "").toLowerCase());
  return lines.slice(1).map(line => {
    const fields = [];
    let cur = "", inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === "," && !inQuote) { fields.push(cur); cur = ""; }
      else { cur += ch; }
    }
    fields.push(cur);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (fields[i] || "").trim().replace(/^"|"$/g, ""); });
    return obj;
  });
}

function rowToRestaurant(row) {
  const name = row.name || row.title || row.restaurant || "";
  if (!name.trim()) return null;
  const wcRaw = (row.world_class || row.wc || "").toLowerCase();
  const isWC = wcRaw === "true" || wcRaw === "yes" || wcRaw === "1";
  return {
    title: name.trim(),
    faveCity: (row.city || "").trim(),
    faveCountry: (row.country || "").trim(),
    faveRegion: (row.region || "").trim(),
    address: (row.address || "").trim(),
    website: (row.website || row.url || row.link || "").trim(),
    faveNotes: (row.notes || row.comments || row.description || "").trim(),
    fave: true,
    favorite: isWC,
    type: "restaurant",
    source: "import",
  };
}

export default function RestaurantsPage({ user, onBack, initialGroup, autoLocalGroup }) {
const [scanTrigger, setScanTrigger] = useState(0);  
const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendMemo, setSendMemo] = useState("");
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [selectedForSend, setSelectedForSend] = useState(new Set());
  const [editingPath, setEditingPath] = useState(null);
  const [editCity, setEditCity] = useState("");
  const [editCountry, setEditCountry] = useState("");
  const [editRegion, setEditRegion] = useState("");
  const [editRegionSuggestions, setEditRegionSuggestions] = useState([]);
  const [editNotes, setEditNotes] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addCity, setAddCity] = useState("");
  const [addCountry, setAddCountry] = useState("");
  const [addRegion, setAddRegion] = useState("");
  const [addAddress, setAddAddress] = useState("");
  const [addWebsite, setAddWebsite] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [addIsWC, setAddIsWC] = useState(false);
  const [addRegionSuggestions, setAddRegionSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMap, setShowMap] = useState(false);

  // Add to trip state
  const [trips, setTrips] = useState([]);
  const [showAddToTrip, setShowAddToTrip] = useState(false);
  const [addToTripRestaurant, setAddToTripRestaurant] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [addToTripDate, setAddToTripDate] = useState("");
  const [addToTripTime, setAddToTripTime] = useState("");
  const [addingToTrip, setAddingToTrip] = useState(false);

  // Group filter (set when navigating from "Local restaurants" button on a trip)
  const [groupFilter, setGroupFilter] = useState(initialGroup || null);

  // User location for distance indicators
  const [userLocation, setUserLocation] = useState(null);
  const [sortByDistance, setSortByDistance] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {} // silently ignore denial
    );
  }, []);

  // Spreadsheet import state
  const [importRows, setImportRows] = useState(null);
  const [importSkipped, setImportSkipped] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const fileInputRef = useRef(null);

  // Load trips for "Add to trip" picker
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "trips"), where("memberIds", "array-contains", user.uid))
        );
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.startDate || "").localeCompare(a.startDate || ""));
        setTrips(data);
      } catch (e) {
        console.warn("Trips load failed:", e);
      }
    };
    fetchTrips();
  }, [user.uid]);

  const load = useCallback(async () => {
    try {
      const [segSnap, savedSnap] = await Promise.all([
        getDocs(query(
          collectionGroup(db, "segments"),
          where("fave", "==", true),
          where("type", "==", "restaurant")
        )),
        getDocs(collection(db, "users", user.uid, "savedRestaurants")),
      ]);

      const fromSegments = segSnap.docs
        .map(d => ({ ...d.data(), _path: d.ref.path, _source: "segment" }))
        .filter(seg => !(seg.hiddenFor || []).includes(user.uid));

      const fromSaved = savedSnap.docs
        .map(d => ({ ...d.data(), _path: d.ref.path, _source: "manual" }))
        .filter(r => r.source !== "trip"); // skip trip-synced entries, already covered by segments query

      const combined = [...fromSegments, ...fromSaved]
        .sort((a, b) => (a.title || "").localeCompare(b.title || ""));

      setRestaurants(combined);
    } catch (e) {
      console.warn("Restaurants load failed:", e);
    }
    setLoading(false);
  }, [user.uid]);

  useEffect(() => { load(); }, [load]);

  // When autoLocalGroup is set, wait for restaurants + location, then pick nearest group
  useEffect(() => {
    if (!autoLocalGroup || loading || restaurants.length === 0 || !userLocation) return;
    if (groupFilter) return; // already set

    const distMi = (lat1, lng1, lat2, lng2) => {
      const R = 3958.8;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    let nearestGroup = null, nearestDist = Infinity;
    for (const r of restaurants) {
      if (!r.lat || !r.lng) continue;
      const key = r.faveRegion || r.faveCountry || r.faveCity || r.wcCity || r.destinationCity
        || extractCityFromAddress(r.address);
      if (!key) continue;
      const d = distMi(userLocation.lat, userLocation.lng, r.lat, r.lng);
      if (d < nearestDist) { nearestDist = d; nearestGroup = key; }
    }
    if (nearestGroup) { setGroupFilter(nearestGroup); setSortByDistance(true); }
  }, [autoLocalGroup, loading, restaurants, userLocation, groupFilter]);

  const allFaveRegions = {};
  for (const r of restaurants) {
    if (r.faveCountry && r.faveRegion) {
      const key = r.faveCountry.toLowerCase();
      if (!allFaveRegions[key]) allFaveRegions[key] = new Set();
      allFaveRegions[key].add(r.faveRegion);
    }
  }
  for (const key of Object.keys(allFaveRegions)) {
    allFaveRegions[key] = [...allFaveRegions[key]].sort();
  }

  const searchLower = search.toLowerCase().trim();

  const searchFiltered = searchLower
    ? restaurants.filter(r => {
        const region = (r.faveRegion || "").toLowerCase();
        const city = (r.faveCity || r.wcCity || r.destinationCity || "").toLowerCase();
        const country = (r.faveCountry || "").toLowerCase();
        const address = (r.address || "").toLowerCase();
        const name = (r.title || "").toLowerCase();
        return region.includes(searchLower) || city.includes(searchLower)
          || country.includes(searchLower) || address.includes(searchLower)
          || name.includes(searchLower);
      })
    : restaurants;

  const filtered = groupFilter
    ? searchFiltered.filter(r => {
        const key = r.faveRegion || r.faveCountry || r.faveCity || r.wcCity || r.destinationCity
          || extractCityFromAddress(r.address) || "__unclassified__";
        return key === groupFilter;
      })
    : searchFiltered;

  const grouped = {};
  for (const r of filtered) {
    let key;
    if (r.faveRegion) key = r.faveRegion;
    else if (r.faveCountry) key = r.faveCountry;
    else if (r.faveCity || r.wcCity || r.destinationCity) key = r.faveCity || r.wcCity || r.destinationCity;
    else key = extractCityFromAddress(r.address) || "__unclassified__";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  }

  const distanceMiles = (lat1, lng1, lat2, lng2) => {
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const getMinDist = (rests) => {
    if (!userLocation) return Infinity;
    let min = Infinity;
    for (const r of rests) {
      if (r.lat && r.lng) {
        const d = distanceMiles(userLocation.lat, userLocation.lng, r.lat, r.lng);
        if (d < min) min = d;
      }
    }
    return min;
  };

  const sortedGroups = Object.entries(grouped)
    .filter(([k]) => k !== "__unclassified__")
    .sort(([a, aRests], [b, bRests]) => {
      if (sortByDistance && userLocation) {
        const da = getMinDist(aRests);
        const db_ = getMinDist(bRests);
        if (da !== db_) return da - db_;
      }
      return a.localeCompare(b);
    })
    .map(([group, rests]) => {
      if (!sortByDistance || !userLocation) return [group, rests];
      const withDist = rests.map(r => ({
        r,
        dist: (r.lat && r.lng)
          ? distanceMiles(userLocation.lat, userLocation.lng, r.lat, r.lng)
          : Infinity,
      }));
      withDist.sort((a, b) => a.dist - b.dist);
      return [group, withDist.map(x => x.r)];
    });

  const unclassified = grouped["__unclassified__"] || [];

  // Run on page load after restaurants load — throttled to 3 at a time
  const processReservations = useCallback(async (list) => {
  const toProcess = list.filter(r =>
    (!r.reservationChecked || !r.coordsChecked) && r._source && r._path
  );
  if (!toProcess.length) return;

  for (let i = 0; i < toProcess.length; i += 3) {
    const batch = toProcess.slice(i, i + 3);
    await Promise.all(batch.map(async (r) => {
      try {
        const res = await fetch("/api/check-reservation", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: r.title,
            city: r.faveCity || r.wcCity || r.destinationCity || "",
            country: r.faveCountry || "",
            website: r.website || r.link || "",
            address: r.address || "",
          }),
        });
        const data = await res.json();
        const updates = { reservationChecked: true, coordsChecked: true };
        if (typeof data.lat === "number" && typeof data.lng === "number") {
          updates.lat = data.lat;
          updates.lng = data.lng;
        }
        if (data.placeId) updates.placeId = data.placeId;
        if (data.website && !r.website) updates.website = data.website;
        if (data.reservationUrl) updates.reservationUrl = data.reservationUrl;
        await updateDoc(doc(db, r._path), updates);
      } catch (e) {
        console.warn("Reservation check failed for", r.title, e);
        try {
          await updateDoc(doc(db, r._path), { reservationChecked: true });
        } catch (e2) {
          console.warn("Could not mark reservationChecked for", r.title, e2);
        }
      }
    }));
  }
  load();
}, [load]);

  useEffect(() => {
  if (restaurants.length > 0) {
    processReservations(restaurants);
  }
}, [restaurants.length, processReservations, scanTrigger]); // eslint-disable-line react-hooks/exhaustive-deps
  const resetReservations = useCallback(async () => {
    if (!window.confirm(`Reset reservation data for all ${restaurants.length} restaurants and re-scan? This may take a few minutes.`)) return;
    const updates = restaurants
      .filter(r => r._path)
      .map(r => updateDoc(doc(db, r._path), { reservationChecked: false, coordsChecked: false, reservationUrl: "" }));
    await Promise.all(updates);
    await load(); setScanTrigger(t => t + 1);
    alert("Reset done. Scanning will begin automatically.");
  }, [restaurants, load]);

  const siteUrl = (r) => {
    const url = r.link || r.website;
    if (!url) return null;
    return url.startsWith("http") ? url : `https://${url}`;
  };

  const openSendModal = () => {
    setSelectedForSend(new Set(filtered.map((_, i) => i)));
    setShowSendModal(true);
  };

  const toggleSendSelect = (idx) => {
    setSelectedForSend(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleSend = async () => {
    if (!sendTo.trim()) return;
    const toSend = filtered.filter((_, i) => selectedForSend.has(i));
    if (!toSend.length) return;
    setSending(true);
    try {
      await fetch("/api/send-restaurants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: sendTo.trim(), restaurants: toSend, searchTerm: searchLower || null, memo: sendMemo.trim() || null, senderName: user?.displayName || user?.email || "Gulliver Travels", replyTo: user?.email || null }),
      });
      setSendSuccess(true);
      setTimeout(() => { setShowSendModal(false); setSendSuccess(false); setSendTo(""); setSendMemo(""); }, 2000);
    } catch (e) {
      console.error("Send failed:", e);
    }
    setSending(false);
  };

  const openEdit = (r) => {
    setEditingPath(r._path);
    setEditCity(r.faveCity || r.wcCity || r.destinationCity || extractCityFromAddress(r.address) || "");
    setEditCountry(r.faveCountry || extractCountryFromAddress(r.address) || "");
    setEditRegion(r.faveRegion || "");
    setEditNotes(r.faveNotes || r.wcComment || "");
    setEditRegionSuggestions(allFaveRegions[(r.faveCountry || "").toLowerCase()] || []);
  };

  const handleEditCountryChange = (val) => {
    setEditCountry(val);
    setEditRegion("");
    setEditRegionSuggestions(allFaveRegions[val.toLowerCase()] || []);
  };

  const handleSaveEdit = async () => {
    if (!editingPath) return;
    await updateDoc(doc(db, editingPath), {
      faveCity: editCity.trim(),
      faveCountry: editCountry.trim(),
      faveRegion: editRegion.trim(),
      faveNotes: editNotes.trim(),
    });
    setEditingPath(null);
    load();
  };

  const handleAddCountryChange = (val) => {
    setAddCountry(val);
    setAddRegion("");
    setAddRegionSuggestions(allFaveRegions[val.toLowerCase()] || []);
  };

  const resetAddModal = () => {
    setAddTitle(""); setAddCity(""); setAddCountry(""); setAddRegion("");
    setAddAddress(""); setAddWebsite(""); setAddNotes(""); setAddIsWC(false);
    setAddRegionSuggestions([]);
  };

  const handleSaveAdd = async () => {
    if (!addTitle.trim()) return;
    setSaving(true);
    await addDoc(collection(db, "users", user.uid, "savedRestaurants"), {
      title: addTitle.trim(),
      faveCity: addCity.trim(),
      faveCountry: addCountry.trim(),
      faveRegion: addRegion.trim(),
      address: addAddress.trim(),
      website: addWebsite.trim(),
      faveNotes: addNotes.trim(),
      fave: true,
      favorite: addIsWC,
      type: "restaurant",
      source: "manual",
      createdAt: serverTimestamp(),
    });
    setSaving(false);
    setShowAddModal(false);
    resetAddModal();
    load();
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from your restaurant list?`)) return;
    if (r._source === "manual") {
      await deleteDoc(doc(db, r._path));
    } else {
      await updateDoc(doc(db, r._path), { fave: false });
    }
    load();
  };

  const handleMarkClosed = async (r) => {
    if (!window.confirm(`Mark "${r.title}" as permanently closed?`)) return;
    await updateDoc(doc(db, r._path), { permanentlyClosed: true });
    load();
  };


  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportRows(null);
    setImportDone(false);
    try {
      let rows = [];
      const fileName = file.name.toLowerCase();
      if (fileName.endsWith(".csv")) {
        const text = await file.text();
        rows = parseCSV(text);
      } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
        if (!window.XLSX) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            script.onload = resolve; script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        const buffer = await file.arrayBuffer();
        const wb = window.XLSX.read(buffer, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = window.XLSX.utils.sheet_to_json(ws, { defval: "" });
        rows = rows.map(r => {
          const normalized = {};
          for (const [k, v] of Object.entries(r)) {
            normalized[k.toLowerCase().trim()] = String(v || "").trim();
          }
          return normalized;
        });
      } else {
        return;
      }
      // Fetch existing entries for upsert
      const existingSnap = await getDocs(collection(db, "users", user.uid, "savedRestaurants"));
      const existingMap = {};
      existingSnap.docs.forEach(d => {
        const title = (d.data().title || "").toLowerCase().trim();
        existingMap[title] = d.ref;
      });
      const toInsert = [];
      const toUpdate = [];
      let skipped = 0;
      for (const row of rows) {
        const r = rowToRestaurant(row);
        if (!r) { skipped++; continue; }
        const key = r.title.toLowerCase();
        if (existingMap[key]) {
          toUpdate.push({ ref: existingMap[key], data: r });
        } else {
          toInsert.push(r);
        }
      }
      setImportRows({ toInsert, toUpdate });
      setImportSkipped(skipped);
    } catch (err) {
      console.error("Import parse error:", err);
    }
    e.target.value = "";
  };

  const handleConfirmImport = async () => {
    if (!importRows) return;
    const { toInsert, toUpdate } = importRows;
    if (!toInsert.length && !toUpdate.length) return;
    setImporting(true);
    const col = collection(db, "users", user.uid, "savedRestaurants");
    for (const r of toInsert) {
      await addDoc(col, { ...r, createdAt: serverTimestamp() });
    }
    for (const { ref, data } of toUpdate) {
      await updateDoc(ref, data);
    }
    setImporting(false);
    setImportDone(toInsert.length + toUpdate.length);
    setImportRows(null);
    load();
  };


  const openAddToTrip = (r) => {
    setAddToTripRestaurant(r);
    setSelectedTripId("");
    setAddToTripDate(new Date().toISOString().split("T")[0]);
    setAddToTripTime("");
    setShowAddToTrip(true);
  };

  const handleAddToTrip = async () => {
    if (!selectedTripId || !addToTripDate || !addToTripRestaurant) return;
    setAddingToTrip(true);
    const r = addToTripRestaurant;
    await addDoc(collection(db, "trips", selectedTripId, "segments"), {
      type: "restaurant",
      title: r.title,
      address: r.address || "",
      link: r.website || r.link || "",
      detail: r.faveNotes || r.wcComment || "",
      date: addToTripDate,
      time: addToTripTime || "",
      fave: true,
      favorite: r.favorite || false,
      faveCity: r.faveCity || "",
      faveCountry: r.faveCountry || "",
      faveRegion: r.faveRegion || "",
      faveNotes: r.faveNotes || r.wcComment || "",
      createdAt: serverTimestamp(),
      createdBy: user.uid,
    });
    setAddingToTrip(false);
    setShowAddToTrip(false);
    setAddToTripRestaurant(null);
  };


  const renderGroup = (title, rests) => (
    <div key={title} style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 13, fontWeight: 600, color: "var(--ink-mid)",
        textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--border)",
      }}>
        {title}
      </div>
      {rests.map((r, i) => {
        const isLast = i === rests.length - 1;
        const isEditing = editingPath === r._path;
        const isWC = r.favorite;
        const isClosed = r.permanentlyClosed;

        if (isEditing) {
          return (
            <div key={r._path || i} style={{ padding: "12px 0", borderBottom: isLast ? "none" : "1px solid var(--border)" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", marginBottom: 12 }}>{r.title}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">City</label>
                  <input className="form-input" value={editCity} onChange={e => setEditCity(e.target.value)} placeholder="e.g. Chicago, Seville" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Country</label>
                  <input className="form-input" value={editCountry} onChange={e => handleEditCountryChange(e.target.value)} placeholder="e.g. USA, Spain, France" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Region or area</label>
                  <input
                    className="form-input"
                    value={editRegion}
                    onChange={e => setEditRegion(e.target.value)}
                    placeholder={editCountry.trim() ? "e.g. Tuscany, Madrid Metro, South of France" : "Enter country first"}
                    disabled={!editCountry.trim()}
                    style={!editCountry.trim() ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                  />
                  {editRegionSuggestions.length > 0 && !editRegion && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                      {editRegionSuggestions.map(reg => (
                        <button key={reg} onClick={() => setEditRegion(reg)} style={{
                          background: "var(--paper-warm)", border: "1px solid var(--border)",
                          borderRadius: 4, fontSize: 12, padding: "3px 8px",
                          cursor: "pointer", fontFamily: "var(--font-body)", color: "var(--ink-mid)"
                        }}>{reg}</button>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>Used to group and search your restaurants</div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input"
                    value={editNotes}
                    onChange={e => setEditNotes(e.target.value)}
                    placeholder="Impressions, tips, signature dish..."
                    rows={3}
                    style={{ resize: "none", fontFamily: "var(--font-body)", fontSize: 13 }}
                  />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="seg-btn primary" onClick={handleSaveEdit}>Save</button>
                  <button className="seg-btn" onClick={() => setEditingPath(null)}>Cancel</button>
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                  <button onClick={() => handleDelete(r)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#C05621", padding: 0, fontFamily: "var(--font-body)" }}>
                    remove from list
                  </button>
                  {r.permanentlyClosed ? (
                    <button onClick={() => { updateDoc(doc(db, r._path), { permanentlyClosed: false }); load(); }}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-faint)", padding: 0, fontFamily: "var(--font-body)" }}>
                      unmark closed
                    </button>
                  ) : (
                    <button onClick={() => handleMarkClosed(r)}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-faint)", padding: 0, fontFamily: "var(--font-body)" }}>
                      permanently closed
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={r._path || i} style={{ padding: "12px 0", borderBottom: isLast ? "none" : "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                  {isWC ? <span style={{ fontSize: 13 }}>😋</span> : <Icon name="faveBadge" size={14} />}
                  {isClosed && <span style={{ fontSize: 10, color: "#C05621", fontFamily: "var(--font-body)", fontWeight: 500 }}>🚫 closed</span>}
                  {siteUrl(r) ? (
                    <a href={siteUrl(r)} target="_blank" rel="noreferrer"
                      style={{ fontSize: 14, fontWeight: 500, color: "var(--accent)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.title}
                    </a>
                  ) : (
                    <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.title}
                    </div>
                  )}
                </div>
                {r.address && <div style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 2 }}>{r.address}</div>}
                {(r.faveNotes || r.wcComment) && (
                  <div style={{ fontSize: 12, color: "var(--ink-light)", fontStyle: "italic" }}>
                    {r.faveNotes || r.wcComment}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                {r.date && <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>{r.date}</div>}
                {userLocation && r.lat && r.lng && (() => {
                  const mi = distanceMiles(userLocation.lat, userLocation.lng, r.lat, r.lng);
                  const label = mi < 0.1 ? "< 0.1 mi" : mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
                  return <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>📍 {label}</div>;
                })()}
                {r.reservationUrl ? (
                  <a href={r.reservationUrl} target="_blank" rel="noreferrer"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--accent)", padding: 0, fontFamily: "var(--font-body)", textDecoration: "none" }}>
                    🍽️ reserve
                  </a>
                ) : r.reservationChecked && siteUrl(r) ? (
                  <a href={"https://www.google.com/search?q=" + encodeURIComponent((r.title || "") + " " + (r.faveCity || r.wcCity || r.destinationCity || "") + " reservation")}
                    target="_blank" rel="noreferrer"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-faint)", padding: 0, fontFamily: "var(--font-body)", textDecoration: "none" }}>
                    🍽️ reserve?
                  </a>
                ) : null}
                <button onClick={() => openEdit(r)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-faint)", padding: 0, fontFamily: "var(--font-body)" }}>
                  edit
                </button>
                <button onClick={() => openAddToTrip(r)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--accent)", padding: 0, fontFamily: "var(--font-body)" }}>
                  + add to trip
                </button>

              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <header className="header">
        <button className="icon-btn" onClick={onBack}>
          <Icon name="back" size={18} />
        </button>
        <div className="header-logo" style={{ fontSize: 17 }}>Gulliver Travels</div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="seg-btn" onClick={() => setShowImportModal(true)} style={{ fontSize: 13, whiteSpace: "nowrap" }}>
            📂 Add from spreadsheet
          </button>
          <button className="seg-btn primary" onClick={() => setShowAddModal(true)} style={{ fontSize: 13, whiteSpace: "nowrap" }}>
            <Icon name="plus" size={13} /> Add
          </button>
        </div>
      </header>

      <main className="main">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 600, color: "var(--ink)", letterSpacing: -0.5 }}>
            My Recommended Restaurants
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {filtered.length > 0 && (
              <button className="seg-btn" onClick={() => setShowMap(true)} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                🗺 Map
              </button>
            )}
            {filtered.length > 0 && (
              <button className="seg-btn primary" onClick={openSendModal} style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                📤 Send{searchLower ? ` · ${filtered.length}` : ""}
              </button>
            )}
            {user.uid === "7il1LQ1TqRepBSOJbcISMsqmQJ92" && (
              <button onClick={resetReservations}
                title="Re-scan all restaurants for reservation links"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--ink-faint)", padding: "2px 4px" }}>
                ↺
              </button>
            )}
          </div>
        </div>

        <div style={{ position: "relative", marginBottom: 20 }}>
          <input
            className="form-input"
            placeholder="Search by region, city, country, or name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
          <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-faint)", fontSize: 15 }}>🔍</span>
          {search && (
            <button onClick={() => setSearch("")}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-light)", fontSize: 16, padding: 4 }}>
              ×
            </button>
          )}
        </div>

        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} style={{ display: "none" }} />



        {/* Import preview */}
        {importRows && (
          <div style={{
            background: "var(--paper-warm)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)", padding: "14px", marginBottom: 16,
          }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)", marginBottom: 4 }}>
              {importRows.toInsert.length} new · {importRows.toUpdate.length} to update
            </div>
            {importSkipped > 0 && (
              <div style={{ fontSize: 12, color: "var(--ink-light)", marginBottom: 6 }}>
                {importSkipped} row{importSkipped !== 1 ? "s" : ""} skipped (no name)
              </div>
            )}
            {[...importRows.toInsert, ...importRows.toUpdate.map(u => u.data)].slice(0, 5).map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: "var(--ink-mid)", paddingTop: 3 }}>
                {r.favorite ? "😋 " : "⭐ "}{r.title}{r.faveRegion ? ` — ${r.faveRegion}` : r.faveCity ? ` — ${r.faveCity}` : ""}
              </div>
            ))}
            {(importRows.toInsert.length + importRows.toUpdate.length) > 5 && (
              <div style={{ fontSize: 12, color: "var(--ink-faint)", paddingTop: 3 }}>+ {importRows.toInsert.length + importRows.toUpdate.length - 5} more...</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="submit-btn" onClick={handleConfirmImport} disabled={importing} style={{ flex: 1 }}>
                {importing ? "Importing..." : `Import ${importRows.toInsert.length + importRows.toUpdate.length} restaurant${importRows.toInsert.length + importRows.toUpdate.length !== 1 ? "s" : ""}`}
              </button>
              <button className="seg-btn" onClick={() => { setImportRows(null); setImportSkipped(0); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {importDone && (
          <div style={{ fontSize: 13, color: "var(--ink)", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            ✅ Imported {importDone} restaurant{importDone !== 1 ? "s" : ""}
            <button className="seg-btn" style={{ fontSize: 12 }} onClick={() => setImportDone(false)}>Dismiss</button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--ink-light)" }}>Loading your restaurants...</div>
        ) : restaurants.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🍽</div>
            <h3>No restaurants yet</h3>
            <p>Tap Add to enter one, or fave a restaurant from any trip segment.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--ink-light)" }}>No restaurants match "{search}"</div>
        ) : (
          <>
            {groupFilter && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
                background: "var(--surface-2, #f5f5f5)", borderRadius: 8, padding: "6px 10px" }}>
                <span style={{ fontSize: 13 }}>📍 Showing: <strong>{groupFilter}</strong></span>
                <button onClick={() => setGroupFilter(null)}
                  style={{ marginLeft: "auto", fontSize: 12, color: "var(--ink-light)",
                    background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}>
                  Show all ✕
                </button>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", marginBottom: 16, gap: 8 }}>
              <span style={{ fontSize: 12, color: "var(--ink-faint)" }}>
                {filtered.length} restaurant{filtered.length !== 1 ? "s" : ""}
                {searchLower ? ` matching "${search}"` : ""}
              </span>
              {userLocation && !groupFilter && (
                <button
                  onClick={() => setSortByDistance(s => !s)}
                  style={{
                    marginLeft: "auto", fontSize: 12, padding: "2px 8px",
                    borderRadius: 12, border: "1px solid var(--border)",
                    background: sortByDistance ? "var(--accent, #3B6ECC)" : "transparent",
                    color: sortByDistance ? "#fff" : "var(--ink-light)",
                    cursor: "pointer", whiteSpace: "nowrap",
                  }}
                >
                  📍 By distance
                </button>
              )}
            </div>
            {sortedGroups.map(([group, rests]) => renderGroup(group, rests))}
            {unclassified.length > 0 && renderGroup("Unclassified", unclassified)}
          </>
        )}
      </main>

      {showMap && (
        <RestaurantsMap
          restaurants={filtered}
          filterLabel={searchLower ? search.trim() : ""}
          onClose={() => setShowMap(false)}
        />
      )}

      {/* Add modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title"><Icon name="faveBadge" size={15} /> Add restaurant</div>
              <button className="modal-close" onClick={() => { setShowAddModal(false); resetAddModal(); }}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="form-input" value={addTitle} onChange={e => setAddTitle(e.target.value)} placeholder="Restaurant name" autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={addCity} onChange={e => setAddCity(e.target.value)} placeholder="e.g. Chicago, Seville" />
            </div>
            <div className="form-group">
              <label className="form-label">Country</label>
              <input className="form-input" value={addCountry} onChange={e => handleAddCountryChange(e.target.value)} placeholder="e.g. USA, Spain, France" />
            </div>
            <div className="form-group">
              <label className="form-label">Region or area</label>
              <input
                className="form-input"
                value={addRegion}
                onChange={e => setAddRegion(e.target.value)}
                placeholder={addCountry.trim() ? "e.g. Tuscany, Madrid Metro, South of France" : "Enter country first"}
                disabled={!addCountry.trim()}
                style={!addCountry.trim() ? { opacity: 0.5, cursor: "not-allowed" } : {}}
              />
              {addRegionSuggestions.length > 0 && !addRegion && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {addRegionSuggestions.map(reg => (
                    <button key={reg} onClick={() => setAddRegion(reg)} style={{
                      background: "var(--paper-warm)", border: "1px solid var(--border)",
                      borderRadius: 4, fontSize: 12, padding: "3px 8px",
                      cursor: "pointer", fontFamily: "var(--font-body)", color: "var(--ink-mid)"
                    }}>{reg}</button>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>Used to group and search your restaurants</div>
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={addAddress} onChange={e => setAddAddress(e.target.value)} placeholder="Full street address" />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input className="form-input" value={addWebsite} onChange={e => setAddWebsite(e.target.value)} placeholder="https://..." />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-input"
                value={addNotes}
                onChange={e => setAddNotes(e.target.value)}
                placeholder="Impressions, tips, signature dish..."
                rows={3}
                style={{ resize: "none", fontFamily: "var(--font-body)", fontSize: 14 }}
              />
            </div>
            <div
              onClick={() => setAddIsWC(!addIsWC)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 14px", marginBottom: 16,
                background: addIsWC ? "var(--paper-warm)" : "var(--paper-card)",
                border: `1px solid ${addIsWC ? "var(--accent)" : "var(--border)"}`,
                borderRadius: "var(--radius-sm)", cursor: "pointer",
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: "var(--ink)" }}>😋 World Class</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>Truly exceptional — hall of fame level</div>
              </div>
              <div style={{
                width: 44, height: 24, borderRadius: 12,
                background: addIsWC ? "var(--accent)" : "var(--border)",
                transition: "background 0.2s", position: "relative", flexShrink: 0,
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", background: "white",
                  position: "absolute", top: 3, left: addIsWC ? 23 : 3,
                  transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)"
                }} />
              </div>
            </div>
            <button className="submit-btn" onClick={handleSaveAdd} disabled={saving || !addTitle.trim()}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      )}



      {/* Add to trip modal */}
      {showAddToTrip && addToTripRestaurant && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddToTrip(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Add to trip</div>
              <button className="modal-close" onClick={() => setShowAddToTrip(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ fontSize: 14, color: "var(--ink-mid)", marginBottom: 16 }}>
              {addToTripRestaurant.title}
            </div>
            <div className="form-group">
              <label className="form-label">Trip</label>
              <select
                className="form-input"
                value={selectedTripId}
                onChange={e => {
                  const tid = e.target.value;
                  setSelectedTripId(tid);
                  if (tid) {
                    const trip = trips.find(t => t.id === tid);
                    if (trip?.startDate) setAddToTripDate(trip.startDate.substring(0, 10));
                  }
                }}
                style={{ fontFamily: "var(--font-body)" }}
              >
                <option value="">Select a trip...</option>
                {trips.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.startDate ? ` (${t.startDate.substring(0, 7)})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                className="form-input"
                type="date"
                value={addToTripDate}
                onChange={e => setAddToTripDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Time (optional)</label>
              <input
                className="form-input"
                type="time"
                value={addToTripTime}
                onChange={e => setAddToTripTime(e.target.value)}
              />
            </div>
            <button
              className="submit-btn"
              onClick={handleAddToTrip}
              disabled={addingToTrip || !selectedTripId || !addToTripDate}
            >
              {addingToTrip ? "Adding..." : "Add to trip"}
            </button>
          </div>
        </div>
      )}

      {/* Add from spreadsheet modal */}
      {showImportModal && !importRows && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowImportModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">📂 Add from spreadsheet</div>
              <button className="modal-close" onClick={() => setShowImportModal(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a
                href="/gulliver-restaurant-import-template.xlsx"
                download
                className="seg-btn"
                style={{ textDecoration: "none", padding: "12px 16px", textAlign: "left", fontSize: 14 }}
              >
                <div style={{ fontWeight: 500, color: "var(--ink)" }}>📥 Download template</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Get the spreadsheet template with the right columns
                </div>
              </a>
              <button
                className="seg-btn"
                style={{ padding: "12px 16px", textAlign: "left", fontSize: 14 }}
                onClick={() => { setShowImportModal(false); fileInputRef.current?.click(); }}
              >
                <div style={{ fontWeight: 500, color: "var(--ink)" }}>📤 Upload my list</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Import restaurants from a CSV or Excel file
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowSendModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">📤 Email this list</div>
              <button className="modal-close" onClick={() => setShowSendModal(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            {sendSuccess ? (
              <div style={{ textAlign: "center", padding: "24px 0", fontSize: 15, color: "var(--ink)" }}>✅ Sent!</div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Email address</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder={`${user.email} or multiple, comma-separated`}
                    value={sendTo}
                    onChange={e => setSendTo(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSend()}
                    autoFocus
                  />
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-mid)", marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <span>Select restaurants to include:</span>
                  <span style={{ color: "var(--ink-faint)" }}>{selectedForSend.size} of {filtered.length}</span>
                </div>
                <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", marginBottom: 16 }}>
                  {filtered.map((r, i) => (
                    <div key={i} onClick={() => toggleSendSelect(i)} style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                      borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none",
                      cursor: "pointer", background: selectedForSend.has(i) ? "var(--paper-warm)" : "transparent",
                    }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 3, border: "1.5px solid var(--border)",
                        background: selectedForSend.has(i) ? "var(--ink)" : "white",
                        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        {selectedForSend.has(i) && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {r.favorite ? "😋 " : ""}{r.title}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>
                          {r.faveRegion || r.faveCity || r.wcCity || ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="form-group" style={{ marginBottom: 12 }}>
                  <label className="form-label">Add a note <span style={{ fontWeight: 400, color: "var(--ink-light)" }}>(optional)</span></label>
                  <textarea
                    className="form-input"
                    placeholder="These are my favorites from our trip last year..."
                    value={sendMemo}
                    onChange={(e) => setSendMemo(e.target.value)}
                    rows={2}
                    style={{ resize: "vertical", fontFamily: "inherit", fontSize: 13 }}
                  />
                </div>
                <button className="submit-btn" onClick={handleSend} disabled={sending || !sendTo.trim() || selectedForSend.size === 0}>
                  {sending ? "Sending..." : `Send ${selectedForSend.size} restaurant${selectedForSend.size !== 1 ? "s" : ""}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}